import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  combosService,
  productsService,
  Combo,
  ComboItem,
  CreateComboData,
  UpdateComboData,
  Product,
} from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';

// ---------------------------------------------------------------------------
// Validação client-side (defesa em profundidade — backend é a autoridade)
// ---------------------------------------------------------------------------

/** Allowlist do codigo: ^[A-Za-z0-9][A-Za-z0-9_ -]{0,38}[A-Za-z0-9]$ */
const CODIGO_REGEX = /^[A-Za-z0-9][A-Za-z0-9_ -]{0,38}[A-Za-z0-9]$/;

/** Teto de qtd por ComboItem (o backend valida com Joi). */
const QTD_MAX = 50;

/** Teto de periodo (o backend valida com Joi). */
const PERIODO_MAX = 36;

/** Teto de valorUnitario por ComboItem (o backend valida com Joi). */
const VALOR_UNITARIO_MAX = 100_000;

function validateComboForm(data: {
  codigo: string;
  nome: string;
  periodo: number | string;
  composicao: ComboItem[];
}): string | null {
  if (!data.codigo.trim()) return 'Código é obrigatório.';
  if (!CODIGO_REGEX.test(data.codigo.trim())) {
    return 'Código inválido. Deve começar e terminar com letra ou número, e conter apenas letras, números, hífens, underscores ou espaços (2–40 caracteres).';
  }
  if (!data.nome.trim()) return 'Nome é obrigatório.';
  const periodo = Number(data.periodo);
  if (!Number.isInteger(periodo) || periodo < 1) return 'Período deve ser um inteiro ≥ 1.';
  if (periodo > PERIODO_MAX) return `Período não pode exceder ${PERIODO_MAX}.`;
  if (data.composicao.length === 0) return 'A composição deve ter pelo menos um item.';
  for (let i = 0; i < data.composicao.length; i++) {
    const item = data.composicao[i];
    if (!item.productId) return `Item ${i + 1}: selecione um ativo.`;
    const qtd = Number(item.qtd);
    if (!Number.isInteger(qtd) || qtd < 1) return `Item ${i + 1}: quantidade deve ser inteiro ≥ 1.`;
    if (qtd > QTD_MAX) return `Item ${i + 1}: quantidade não pode exceder ${QTD_MAX}.`;
    const valor = Number(item.valorUnitario);
    if (!isFinite(valor) || valor < 0) return `Item ${i + 1}: valor unitário inválido.`;
    if (valor > VALOR_UNITARIO_MAX) return `Item ${i + 1}: valor unitário não pode exceder ${VALOR_UNITARIO_MAX}.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Estado inicial do formulário
// ---------------------------------------------------------------------------
interface ComboFormState {
  codigo: string;
  nome: string;
  periodo: string;
  isActive: boolean;
  composicao: ComboItem[];
}

const EMPTY_FORM: ComboFormState = {
  codigo: '',
  nome: '',
  periodo: '1',
  isActive: true,
  composicao: [],
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const CombosPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('combos', 'editar');
  const [combos, setCombos] = useState<Combo[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCombo, setEditingCombo] = useState<Combo | null>(null);
  const [formData, setFormData] = useState<ComboFormState>({ ...EMPTY_FORM });
  const [formError, setFormError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deletingComboId, setDeletingComboId] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Mapa id → Product para lookups rápidos
  // ---------------------------------------------------------------------------
  const productMap = useMemo<Record<string, Product>>(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );

  // ---------------------------------------------------------------------------
  // IDs de products já usados na composição atual (evita duplicatas)
  // ---------------------------------------------------------------------------
  const usedProductIds = useMemo<Set<string>>(
    () => new Set(formData.composicao.map((item) => item.productId).filter(Boolean)),
    [formData.composicao]
  );

  // ---------------------------------------------------------------------------
  // Valor default da subscription = Σ(qtd × valorUnitario) — informativo
  // ---------------------------------------------------------------------------
  const totalValorUnitario = useMemo(
    () =>
      formData.composicao.reduce((acc, item) => {
        const qtd = Number(item.qtd) || 0;
        const valor = Number(item.valorUnitario) || 0;
        return acc + qtd * valor;
      }, 0),
    [formData.composicao]
  );

  // ---------------------------------------------------------------------------
  // Carregamento de dados
  // ---------------------------------------------------------------------------
  const loadData = async () => {
    try {
      setLoading(true);
      const [combosData, productsData] = await Promise.all([
        combosService.getAll(),
        productsService.getAll(),
      ]);
      setCombos(combosData);
      setProducts(productsData);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Erro ao carregar dados',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ---------------------------------------------------------------------------
  // Diálogo criar / editar
  // ---------------------------------------------------------------------------
  const handleOpenDialog = (combo?: Combo) => {
    setFormError(null);
    if (combo) {
      setEditingCombo(combo);
      setFormData({
        codigo: combo.codigo,
        nome: combo.nome,
        periodo: String(combo.periodo),
        isActive: combo.isActive,
        composicao: combo.composicao.map((item) => ({ ...item })),
      });
    } else {
      setEditingCombo(null);
      setFormData({ ...EMPTY_FORM });
    }
    setOpenDialog(true);
  };

  const resetDialog = () => {
    setOpenDialog(false);
    setEditingCombo(null);
    setFormError(null);
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    resetDialog();
  };

  // ---------------------------------------------------------------------------
  // Editor de composição
  // ---------------------------------------------------------------------------
  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      composicao: [
        ...prev.composicao,
        { productId: '', qtd: 1, valorUnitario: 0 },
      ],
    }));
  };

  const handleRemoveItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      composicao: prev.composicao.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index: number, field: keyof ComboItem, value: string | number) => {
    setFormData((prev) => {
      const newComposicao = [...prev.composicao];
      newComposicao[index] = { ...newComposicao[index], [field]: value };
      return { ...prev, composicao: newComposicao };
    });
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const handleSubmit = async () => {
    const validationError = validateComboForm({
      ...formData,
      periodo: Number(formData.periodo),
    });
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError(null);

    const composicao: ComboItem[] = formData.composicao.map((item) => ({
      productId: item.productId,
      qtd: Number(item.qtd),
      valorUnitario: Number(item.valorUnitario),
    }));

    try {
      setSubmitting(true);
      if (editingCombo) {
        const payload: UpdateComboData = {
          codigo: formData.codigo.trim(),
          nome: formData.nome.trim(),
          periodo: Number(formData.periodo),
          isActive: formData.isActive,
          composicao,
        };
        const result = await combosService.update(editingCombo.id, payload);
        if (result.success) {
          await loadData();
          setSnackbar({ open: true, message: 'Combo atualizado com sucesso!', severity: 'success' });
          resetDialog();
        } else {
          throw new Error(result.error || 'Erro ao atualizar combo');
        }
      } else {
        const payload: CreateComboData = {
          codigo: formData.codigo.trim(),
          nome: formData.nome.trim(),
          periodo: Number(formData.periodo),
          isActive: formData.isActive,
          composicao,
        };
        const result = await combosService.create(payload);
        if (result.success) {
          await loadData();
          setSnackbar({ open: true, message: 'Combo criado com sucesso!', severity: 'success' });
          resetDialog();
        } else {
          throw new Error(result.error || 'Erro ao criar combo');
        }
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Erro ao salvar combo',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Deleção
  // ---------------------------------------------------------------------------
  const handleDelete = async (id: string) => {
    try {
      setDeletingComboId(id);
      const result = await combosService.delete(id);
      if (result.success) {
        setCombos((prev) => prev.filter((c) => c.id !== id));
        setSnackbar({ open: true, message: 'Combo excluído com sucesso!', severity: 'success' });
      } else {
        // Erro amigável quando bloqueado por combo_subscription viva
        throw new Error(result.error || 'Não foi possível excluir o combo');
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Erro ao excluir combo',
        severity: 'error',
      });
    } finally {
      setDeletingComboId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Renderização
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Combos (Planos)
        </Typography>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Combos.'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={!canManage}>
              Novo combo
            </Button>
          </span>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Período</TableCell>
              <TableCell>Composição</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {combos.map((combo) => {
              const rowBusy = deletingComboId === combo.id;
              return (
                <TableRow
                  key={combo.id}
                  hover
                  sx={{ opacity: combo.isActive ? 1 : 0.55 }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {combo.codigo}
                    </Typography>
                  </TableCell>
                  <TableCell>{combo.nome}</TableCell>
                  <TableCell>{combo.periodo}x</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {combo.composicao.map((item, i) => {
                        const product = productMap[item.productId];
                        const inactive = product && !product.isActive;
                        return (
                          <Tooltip
                            key={i}
                            title={
                              inactive
                                ? `Ativo inativo: ${product?.titulo ?? item.productId}`
                                : product?.titulo ?? item.productId
                            }
                          >
                            <Chip
                              label={`${product?.titulo ?? item.productId} ×${item.qtd}`}
                              size="small"
                              color={inactive ? 'warning' : 'default'}
                              icon={inactive ? <WarningIcon /> : undefined}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={combo.isActive ? 'Ativo' : 'Inativo'}
                      color={combo.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={canManage ? 'Editar' : 'Você não tem permissão de edição no módulo Combos.'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(combo)}
                          color="primary"
                          disabled={!canManage || rowBusy}
                        >
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={canManage ? 'Excluir' : 'Você não tem permissão de edição no módulo Combos.'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(combo.id)}
                          color="error"
                          disabled={!canManage || rowBusy}
                        >
                          {rowBusy ? <CircularProgress size={18} /> : <DeleteIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {combos.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum combo cadastrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo criar / editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingCombo ? 'Editar combo' : 'Novo combo'}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Grid container spacing={2}>
              {/* Código */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Código"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  margin="normal"
                  required
                  disabled={submitting}
                  helperText="2 a 40 caracteres. Deve começar e terminar com letra ou número. Pode conter letras, números, hífens ( - ), underscores ( _ ) e espaços no meio."
                  inputProps={{ maxLength: 40 }}
                />
              </Grid>

              {/* Nome */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  margin="normal"
                  required
                  disabled={submitting}
                  helperText="Nome de exibição"
                />
              </Grid>

              {/* Período */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Período (nº de entregas)"
                  type="number"
                  value={formData.periodo}
                  onChange={(e) => setFormData({ ...formData, periodo: e.target.value })}
                  margin="normal"
                  required
                  disabled={submitting}
                  helperText={`Quantas vezes o combo será entregue. Entre 1 e ${PERIODO_MAX}.`}
                  inputProps={{ min: 1, max: PERIODO_MAX, step: 1 }}
                />
              </Grid>

              { /* Valor do combo - informativo */}
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                    fullWidth
                    label="Valor total (R$)"
                    type="number"
                    value={totalValorUnitario}
                    margin="normal"
                    disabled
                    helperText="Valor total do combo calculado como soma de (qtd × valor unitário) dos itens"
                    inputProps={{ readOnly: true }}
                  />
                {/* <Typography variant="body2" color="text.secondary">
                  Valor do combo:
                  <strong>
                    R$ {totalValorUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </strong>
                </Typography> */}
              </Grid>

              {/* Status */}
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label="Ativo"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* Composição */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">Composição</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    size="small"
                    disabled={submitting}
                  >
                    Adicionar item
                  </Button>
                </Box>

                {formData.composicao.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Nenhum item. Clique em "Adicionar item" para compor o plano.
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                    {formData.composicao.map((item, index) => {
                      const selectedProduct = productMap[item.productId];
                      const isInactive = selectedProduct && !selectedProduct.isActive;
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                            p: 2,
                            border: '1px solid',
                            borderColor: isInactive ? 'warning.main' : 'grey.300',
                            borderRadius: 1,
                            bgcolor: isInactive ? 'warning.50' : 'grey.50',
                          }}
                        >
                          {/* Seletor de product — exclui productIds já usados nos outros itens */}
                          <FormControl sx={{ flex: 2 }} size="small" disabled={submitting}>
                            <InputLabel>Ativo</InputLabel>
                            <Select
                              value={item.productId}
                              onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                              label="Ativo"
                            >
                              {products.map((p) => {
                                const alreadyUsed = usedProductIds.has(p.id) && p.id !== item.productId;
                                return (
                                  <MenuItem
                                    key={p.id}
                                    value={p.id}
                                    disabled={alreadyUsed}
                                  >
                                    {p.titulo}
                                    {!p.isActive && ' (inativo)'}
                                    {alreadyUsed && ' — já adicionado'}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>

                          {/* Qtd */}
                          <TextField
                            label="Qtd"
                            type="number"
                            value={item.qtd}
                            onChange={(e) => handleItemChange(index, 'qtd', e.target.value)}
                            size="small"
                            sx={{ width: 90 }}
                            disabled={submitting}
                            inputProps={{ min: 1, max: QTD_MAX, step: 1 }}
                          />

                          {/* Valor unitário */}
                          <TextField
                            label="Valor unit. (R$)"
                            type="number"
                            value={item.valorUnitario}
                            onChange={(e) => handleItemChange(index, 'valorUnitario', e.target.value)}
                            size="small"
                            sx={{ width: 140 }}
                            disabled={submitting}
                            inputProps={{ min: 0, max: VALOR_UNITARIO_MAX, step: 0.01 }}
                          />

                          {isInactive && (
                            <Tooltip title="Ativo inativo — o grant pula este item (RFC §11.j)">
                              <WarningIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}

                          <IconButton
                            color="error"
                            onClick={() => handleRemoveItem(index)}
                            size="small"
                            disabled={submitting}
                            title="Remover item"
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Valor default da subscription — informativo */}
                {formData.composicao.length > 0 && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: 'info.50',
                      border: '1px solid',
                      borderColor: 'info.200',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Valor padrão do combo:&nbsp;
                      <strong>
                        R$ {totalValorUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </strong>
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {formError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting
              ? editingCombo
                ? 'Salvando…'
                : 'Criando…'
              : editingCombo
              ? 'Atualizar'
              : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CombosPage;
